/* Featured Collection Tabs Section */
class FeaturedCollectionTabsSection {
  static sharedResizeObserver = new ResizeObserver((entries) => {
    entries.forEach(entry => {
      const instance = entry.target.instance;
      if (instance && typeof instance.handleResize === 'function') {
        instance.handleResize();
      }
    });
  });

  constructor(container) {
    if (container.dataset.initialized) return;
    this.container = container;
    this.container.instance = this;
    this.container.dataset.initialized = 'true';
    this.sectionId = container.dataset.sectionId;
    this.tabButtons = container.querySelectorAll('.tab-button');
    this.tabPanels = container.querySelectorAll('.tab-panel');
    this.slideshows = new Map();
    this.mobileBreakpoint = 768;
    this.init();
  }

  init() {
    this.setupTabs();
    this.initializeSlideshows();
    this.setupMobileGridSlideshows();
    this.setupResponsiveGridCols();
    this.bindEvents();

    const activePanel = this.container.querySelector('.tab-panel.active');
    if (activePanel) {
      this.initializePanelSlideshow(activePanel);
    }
  }

  setupTabs() {
    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.switchTab(button.dataset.tabIndex);
      });
    });
  }

  switchTab(tabIndex) {
    this.tabButtons.forEach(button => {
      button.classList.remove('active');
      if (button.dataset.tabIndex === tabIndex) {
        button.classList.add('active');
      }
    });

    this.tabPanels.forEach(panel => {
      panel.classList.remove('active');
      if (panel.dataset.tabIndex === tabIndex) {
        panel.classList.add('active');
        this.initializePanelSlideshow(panel);
      }
    });
  }

  initializeSlideshows() {
    const swiperContainers = this.container.querySelectorAll('.featured-collection__slideshow');

    swiperContainers.forEach(swiperContainer => {
      const panel = swiperContainer.closest('.tab-panel');
      if (panel) {
        const panelIndex = panel.dataset.tabIndex;
        if (!this.slideshows.has(panelIndex)) {
          this.slideshows.set(panelIndex, {
            container: swiperContainer,
            instance: null
          });
        }
      }
    });
  }

  initializePanelSlideshow(panel) {
    if (!panel || typeof Swiper === 'undefined') return;

    const panelIndex = panel.dataset.tabIndex;
    const slideshowData = this.slideshows.get(panelIndex);

    if (!slideshowData) return;

    const swiperContainer = slideshowData.container;
    if (swiperContainer._swiperInstance) {
      slideshowData.instance = swiperContainer._swiperInstance;
      return;
    }

    if (slideshowData.instance) return;

    const swiperOptions = JSON.parse(swiperContainer.dataset.swiperOptions || '{}');

    const defaultOptions = {
      slidesPerView: 2,
      spaceBetween: 20,
      navigation: {
        nextEl: swiperContainer.querySelector('.featured-collection-slideshow-next'),
        prevEl: swiperContainer.querySelector('.featured-collection-slideshow-prev'),
      },
      pagination: {
        el: swiperContainer.querySelector('.featured-collection-pagination'),
        type: "progressbar",
      },
      breakpoints: {
        0: { slidesPerView: 1 },
        450: { slidesPerView: 2 },
        768: { slidesPerView: 4, spaceBetween: 30 },
      },
      ...Kinderly.Swiper.getCommonThumbOptions(swiperContainer),
    };

    const finalOptions = { ...defaultOptions, ...swiperOptions };

    try {
      const swiperInstance = new Swiper(swiperContainer, finalOptions);
      swiperContainer._swiperInstance = swiperInstance;
      slideshowData.instance = swiperInstance;
    } catch (error) {
      console.error('FeaturedCollection Slideshow init error:', error);
    }
  }

  setupMobileGridSlideshows() {
    const swiperContainers = this.container.querySelectorAll('.featured-collection__slideshow');
    
    swiperContainers.forEach(swiperContainer => {
      const panel = swiperContainer.closest('.tab-panel');
      if (!panel) {
        if (typeof Swiper === 'undefined') return;
        if (swiperContainer.swiper) return;

        const swiperOptions = JSON.parse(swiperContainer.dataset.swiperOptions || '{}');
        try {
          new Swiper(swiperContainer, swiperOptions);
        } catch (e) {}
      }
    });
  }

  setupResponsiveGridCols() {
    const gridContainers = this.container.querySelectorAll('.featured-collection__grid');

    gridContainers.forEach(gridContainer => {
      const desktopCols = gridContainer.dataset.desktopCols || 4;
      const mobileCols = gridContainer.dataset.mobileCols || 2;
      gridContainer.style.setProperty('--desktop-cols', desktopCols);
      gridContainer.style.setProperty('--mobile-cols', mobileCols);
    });
  }

  bindEvents() {
    FeaturedCollectionTabsSection.sharedResizeObserver.observe(this.container);
  }

  handleResize() {
    this.slideshows.forEach(slideshowData => {
      if (slideshowData.instance) {
        slideshowData.instance.update();
      }
    });
  }

  updateThumbPosition(swiper, swiperContainer) {
    const star = swiperContainer.querySelector('.featured-collection-star-thumb');
    const fill = swiperContainer.querySelector('.swiper-pagination-progressbar-fill');

    const slides = swiperContainer.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
    const total = slides.length || 1;

    const currentIndex = swiper.realIndex || swiper.activeIndex || 0;
    const position = (currentIndex + 1) / total;

    if (star) {
      star.style.left = `${position * 100}%`;
    }
    if (fill) {
      fill.style.transform = `scaleX(${position})`;
      fill.style.webkitTransform = `scaleX(${position})`;
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    FeaturedCollectionTabsSection.sharedResizeObserver.unobserve(this.container);
    this.slideshows.forEach(slideshowData => {
      if (slideshowData.instance) {
        slideshowData.instance.destroy();
        slideshowData.instance = null;
      }
    });
    this.slideshows.clear();
    this.container.dataset.initialized = '';
  }
}

/* Related Products Section Handler */

class RelatedProductsSection {
  constructor(container) {
    if (container.dataset.initialized) return;
    this.container = container;
    this.container.instance = this;
    this.container.dataset.initialized = 'true';
    this.sectionId = container.dataset.sectionId;
    this.recommendationsContainer = container.querySelector(`[id^="product-recommendations-"]`);
    this.swiperInstance = null;

    if (this.recommendationsContainer) {
      this.init();
    }
  }

  init() {
    const url = this.recommendationsContainer.dataset.url;

    const initLogic = () => {
      if (url && this.recommendationsContainer.querySelector('.loading-placeholder')) {
        this.loadRecommendations(url);
      } else {
        setTimeout(() => {
          this.initializeSwiper();
        }, 100);
      }
    };

    if (window.Shopify && window.Shopify.designMode) {
      initLogic();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        initLogic();
        observer.unobserve(this.container);
      }
    }, { rootMargin: '200px' });
    observer.observe(this.container);
  }

  async loadRecommendations(url) {
    try {
      const response = await fetch(url);
      const data = await response.text();

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = data;

      const newRecommendations = tempDiv.querySelector(`[id^="product-recommendations-"]`);

      if (newRecommendations && newRecommendations.innerHTML.trim()) {
        this.recommendationsContainer.innerHTML = newRecommendations.innerHTML;

        setTimeout(() => {
          this.initializeSwiper();

          if (typeof ProductCardSwatches !== 'undefined') {
            new ProductCardSwatches();
          }

          if (typeof window.initSectionAnimations === 'function') {
            window.initSectionAnimations(`#${this.sectionId}`);
          }
        }, 150);
      } else {
        this.recommendationsContainer.innerHTML = `
          <div style="text-align: center; padding: 20px;">
            <p>No related products available.</p>
          </div>
        `;
      }
    } catch (error) {
      this.recommendationsContainer.innerHTML = `
        <div style="text-align: center; padding: 20px;">
          <p>Unable to load recommendations.</p>
        </div>
      `;
    }
  }

  initializeSwiper() {
    if (typeof Swiper === 'undefined') {
      return;
    }

    const swiperContainers = this.container.querySelectorAll('.featured-collection__slideshow');

    swiperContainers.forEach(swiperContainer => {
      if (swiperContainer._swiperInstance) {
        swiperContainer._swiperInstance.destroy(true, true);
        swiperContainer._swiperInstance = null;
      }

      const swiperOptions = JSON.parse(swiperContainer.dataset.swiperOptions || '{}');
      const resolvedOptions = { ...swiperOptions };

      resolvedOptions.navigation = {
        nextEl: swiperContainer.querySelector('.featured-collection-slideshow-next'),
        prevEl: swiperContainer.querySelector('.featured-collection-slideshow-prev'),
      };

      resolvedOptions.pagination = {
        el: swiperContainer.querySelector('.featured-collection-pagination'),
        type: 'progressbar',
      };

      try {
        const swiperInstance = new Swiper(swiperContainer, {
          ...resolvedOptions,
          on: {
            init: (swiper) => {
              this.updateThumbPosition(swiper, swiperContainer);
            },
            slideChange: (swiper) => {
              this.updateThumbPosition(swiper, swiperContainer);
            },
            setTransition: (swiper, duration) => {
              const star = swiperContainer.querySelector('.featured-collection-star-thumb');
              if (star) {
                star.style.transitionDuration = `${duration}ms`;
              }
            }
          }
        });
        swiperContainer._swiperInstance = swiperInstance;
        this.swiperInstance = swiperInstance;
      } catch (error) {
        console.error('RelatedProducts Swiper init error:', error);
      }
    });
  }

  updateThumbPosition(swiper, swiperContainer) {
    const star = swiperContainer.querySelector('.featured-collection-star-thumb');
    const fill = swiperContainer.querySelector('.swiper-pagination-progressbar-fill');

    const slides = swiperContainer.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
    const total = slides.length || 1;

    const currentIndex = swiper.realIndex || swiper.activeIndex || 0;
    const position = (currentIndex + 1) / total;

    if (star) {
      star.style.left = `${position * 100}%`;
    }
    if (fill) {
      fill.style.transform = `scaleX(${position})`;
      fill.style.webkitTransform = `scaleX(${position})`;
    }
  }

  destroy() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    this.container.dataset.initialized = '';
  }
}

/* Initialize all sections on DOM ready */
const initFeaturedSections = (retryCount = 0) => {
  if (typeof Swiper === 'undefined') {
    if (retryCount < 10) {
      setTimeout(() => initFeaturedSections(retryCount + 1), 200);
    }
    return;
  }

  const featuredTabsSections = document.querySelectorAll('.featured-collection-section');

  featuredTabsSections.forEach(section => {
    if (section.querySelector('.tabs-wrapper')) {
      new FeaturedCollectionTabsSection(section);
    } else if (section.querySelector('[id^="product-recommendations-"]')) {
      new RelatedProductsSection(section);
    }
  });
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(() => initFeaturedSections());
} else {
  setTimeout(initFeaturedSections, 700);
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.featured-collection-section');
  if (section) {
    if (section.querySelector('.tabs-wrapper')) {
      new FeaturedCollectionTabsSection(section);
    } else if (section.querySelector('[id^="product-recommendations-"]')) {
      new RelatedProductsSection(section);
    }
  }
});

/* Shopify theme editor support */
document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('.featured-collection-section');
  if (section && section.featuredTabsInstance) {
    section.featuredTabsInstance.destroy();
  }
  if (section && section.relatedProductsInstance) {
    section.relatedProductsInstance.destroy();
  }
});

document.addEventListener('shopify:section:select', (event) => {
  const section = event.target.querySelector('.featured-collection-section');
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
});

document.addEventListener('shopify:block:select', (event) => {
  const blockId = event.detail.blockId;
  const section = event.target.querySelector('.featured-collection-section');

  if (section) {
    const tabButton = section.querySelector(`[data-block-id="${blockId}"]`);
    if (tabButton && tabButton.classList.contains('tab-button')) {
      const tabIndex = tabButton.dataset.tabIndex;
      const instance = section.featuredTabsInstance;
      if (instance) {
        instance.switchTab(tabIndex);
      }
    }
  }
});

/* Export for module usage */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { FeaturedCollectionTabsSection, RelatedProductsSection };
}

/* Sale Slider Scrolling Handler */
class ScrollingHandler extends HTMLElement {
  constructor() {
    super();
    this.width = 0;
    this.scrolling = this.querySelector('.sale-scrolling-inner');

    const initMarquee = () => {
      this.run();
      this.resizeObserver = new ResizeObserver(() => this.run());
      this.resizeObserver.observe(this);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initMarquee());
    } else {
      setTimeout(initMarquee, 200);
    }
  }

  run() {
    if (!this.scrolling) return;

    this.scrolling.classList.remove('sale-scrolling-animation');
    this.querySelectorAll('.sale-scrolling-dup').forEach(e => e.remove());

    void this.scrolling.offsetWidth;

    let wrap = this.closest('.sale-scrolling-wrapper');
    if (!wrap) return;

    const containerWidth = wrap.offsetWidth;
    const textWidth = this.scrolling.scrollWidth;

    if (textWidth === 0) {
      setTimeout(() => this.run(), 100);
      return;
    }

    const need = Math.ceil(containerWidth / textWidth) + 2;

    for (let i = 0; i < need; i++) {
      let clone = this.scrolling.cloneNode(true);
      clone.classList.add('sale-scrolling-dup', 'sale-scrolling-animation');
      this.appendChild(clone);
    }

    this.scrolling.classList.add('sale-scrolling-animation');
  }
}

customElements.define("scrolling-item", ScrollingHandler);

/* Product Card Variant Swatches & Add to Cart */
class ProductCardSwatches {
  constructor() {
    this.cartType = document.body.dataset.cartType || 'drawer';
    this.init();
  }

  init() {
    this.bindSwatchEvents();
    this.bindAddToCartEvents();
  }

  bindSwatchEvents() {
    document.addEventListener('click', (e) => {
      const swatchItem = e.target.closest('.card-swatch-item');
      if (swatchItem) {
        e.preventDefault();
        e.stopPropagation();
        this.handleSwatchClick(swatchItem);
      }
    });
  }

  handleSwatchClick(swatchItem) {
    const optionValue = swatchItem.dataset.optionValue;
    const optionPosition = parseInt(swatchItem.dataset.optionPosition);
    const productId = swatchItem.dataset.productId;
    const productCard = swatchItem.closest('.card-wrapper');
    if (!productCard) return;

    const optionGroup = swatchItem.closest('.card-variant-swatches');
    if (optionGroup) {
      const groupSwatches = optionGroup.querySelectorAll('.card-swatch-item');
      groupSwatches.forEach(swatch => {
        swatch.classList.remove('active');
      });
      swatchItem.classList.add('active');
    }

    const selectedOptions = this.getSelectedOptions(productCard);
    const variantsData = this.getProductVariantsData(productCard, productId);
    if (!variantsData) return;

    const matchingVariant = this.findMatchingVariant(variantsData.variants, selectedOptions);

    if (matchingVariant) {
      const variantImage = matchingVariant.featured_image?.src;
      if (variantImage) {
        const mainImage = productCard.querySelector('.card-main-image');
        if (mainImage) {
          mainImage.style.opacity = '0.5';
          setTimeout(() => {
            mainImage.src = variantImage;
            mainImage.srcset = variantImage;
            mainImage.style.opacity = '1';
          }, 150);
        }
      }

      const variantInput = productCard.querySelector('.variant-id-input');
      if (variantInput) {
        variantInput.value = matchingVariant.id;
        variantInput.dataset.variantId = matchingVariant.id;
        variantInput.dataset.requiresSellingPlan = matchingVariant.requires_selling_plan;
      }

      const addToCartBtn = productCard.querySelector('.add-to-cart');
      if (addToCartBtn) {
        if (matchingVariant.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.classList.remove('disabled');
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('disabled');
        }
      }

      this.updatePrice(productCard, matchingVariant);
      this.updateOptionAvailability(productCard, variantsData.variants, selectedOptions);
    }
  }

  getSelectedOptions(productCard) {
    const selectedOptions = {};
    const optionGroups = productCard.querySelectorAll('.card-variant-swatches');

    optionGroups.forEach(group => {
      const activeButton = group.querySelector('.card-swatch-item.active');
      if (activeButton) {
        const position = parseInt(activeButton.dataset.optionPosition);
        const value = activeButton.dataset.optionValue;
        selectedOptions[`option${position}`] = value;
      }
    });

    return selectedOptions;
  }

  getProductVariantsData(productCard, productId) {
    const dataScript = productCard.querySelector(`.product-variants-data[data-product-id="${productId}"]`);
    if (!dataScript) return null;

    try {
      return JSON.parse(dataScript.textContent);
    } catch (e) {
      return null;
    }
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find(variant => {
      return Object.keys(selectedOptions).every(optionKey => {
        return variant[optionKey] === selectedOptions[optionKey];
      });
    });
  }

  updatePrice(productCard, variant) {
    const priceWrapper = productCard.querySelector('.card-price-wrapper');
    if (!priceWrapper) return;

    const priceElement = priceWrapper.querySelector('.price-item--regular, .price__regular .price-item');
    if (priceElement) {
      priceElement.textContent = this.formatMoney(variant.price);
    }

    const compareElement = priceWrapper.querySelector('.price-item--sale, .price__sale .price-item');
    if (compareElement && variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareElement.textContent = this.formatMoney(variant.compare_at_price);
      compareElement.style.display = '';
    } else if (compareElement) {
      compareElement.style.display = 'none';
    }

    const unitPriceEl = priceWrapper.querySelector('[data-unit-price]');
    if (unitPriceEl) {
      if (variant.unit_price) {
        const unitPriceItem = unitPriceEl.querySelector('.price-item--last');
        if (unitPriceItem) {
          const spans = unitPriceItem.querySelectorAll('span:not(.visually-hidden):not([aria-hidden])');
          if (spans.length >= 2) {
            spans[0].innerHTML = this.formatMoney(variant.unit_price);
            const refValue = variant.unit_price_measurement.reference_value !== 1 ? variant.unit_price_measurement.reference_value : '';
            spans[1].innerHTML = `${refValue}${variant.unit_price_measurement.reference_unit}`;
          }
        }
        unitPriceEl.classList.remove('hide');
      } else {
        unitPriceEl.classList.add('hide');
      }
    }
  }

  updateOptionAvailability(productCard, variants, selectedOptions) {
    const optionGroups = productCard.querySelectorAll('.card-variant-swatches');

    optionGroups.forEach(group => {
      const optionPosition = parseInt(group.dataset.optionPosition);
      const optionKey = `option${optionPosition}`;
      const buttons = group.querySelectorAll('.card-swatch-item');

      buttons.forEach(button => {
        const optionValue = button.dataset.optionValue;
        const testOptions = { ...selectedOptions };
        testOptions[optionKey] = optionValue;

        const availableVariant = variants.find(variant => {
          return Object.keys(testOptions).every(key => {
            return variant[key] === testOptions[key];
          }) && variant.available;
        });

        if (availableVariant) {
          button.dataset.variantAvailable = 'true';
          button.classList.remove('unavailable');
        } else {
          button.dataset.variantAvailable = 'false';
          button.classList.add('unavailable');
        }
      });
    });
  }

  formatMoney(cents) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents);
    }
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  bindAddToCartEvents() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('.product-card-form');
      if (form) {
        e.preventDefault();
        this.handleAddToCart(form);
      }
    });

    document.addEventListener('click', (e) => {
      const addToCartBtn = e.target.closest('.add-to-cart');
      if (addToCartBtn && addToCartBtn.dataset.addToCart !== undefined) {
        const form = addToCartBtn.closest('.product-card-form');
        if (form) {
          e.preventDefault();
          this.handleAddToCart(form);
        }
      }
    });
  }

  async handleAddToCart(form) {
    const variantInput = form.querySelector('.variant-id-input');
    const addToCartBtn = form.querySelector('.add-to-cart');

    if (!variantInput || !addToCartBtn) return;

    const requiresSellingPlan = variantInput.dataset.requiresSellingPlan === 'true' || variantInput.getAttribute('data-requires-selling-plan') === 'true';
    if (requiresSellingPlan) {
      let message = window.productStrings?.sellingPlanRequired || 'This product requires a subscription. Please [select a plan here].';
      const productUrl = form.dataset.productUrl || form.getAttribute('data-product-url');
      const linkLabel = message.match(/\[(.*?)\]/)?.[1] || 'select a plan here';
      const linkHtml = `<a href="${productUrl || '#'}" style="text-decoration: underline; font-weight: bold;">${linkLabel}</a>`;
      message = message.replace(/\[.*?\]/, linkHtml);
      
      if (window.ajaxCart && typeof window.ajaxCart.showError === 'function') {
        window.ajaxCart.showError(message);
      } else {
        alert(`${message.replace(/<[^>]*>?/gm, '')}: ${window.location.origin}${productUrl || ''}`);
      }
      return;
    }

    const variantId = variantInput.value;
    const quantity = form.querySelector('input[name="quantity"]')?.value || 1;

    if (!variantId) {
      return;
    }

    addToCartBtn.disabled = true;
    const originalHTML = addToCartBtn.innerHTML;
    addToCartBtn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="spinner">
        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="2" stroke-dasharray="43.98" stroke-dashoffset="10.995" fill="none" opacity="0.25"/>
        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="2" stroke-dasharray="43.98" stroke-dashoffset="32.985" fill="none">
          <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="1s" repeatCount="indefinite"/>
        </circle>
      </svg>
    `;

    const formData = { id: variantId, quantity: parseInt(quantity) };

    try {
      if (window.ajaxCart && typeof window.ajaxCart.addToCart === 'function') {
        const result = await window.ajaxCart.addToCart(formData, true);
        if (result && !result.error) {
          this.showAddedFeedback(addToCartBtn, originalHTML);
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          this.updateCartCount(cart.item_count);
        } else {
          addToCartBtn.disabled = false;
          addToCartBtn.innerHTML = originalHTML;
        }
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.description || 'Failed to add to cart');
        }

        const itemAdded = await response.json();
        this.showAddedFeedback(addToCartBtn, originalHTML);
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        this.updateCartCount(cart.item_count);

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item: itemAdded, cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:add', { detail: { item: itemAdded, cart: cart } }));
        if (this.cartType === 'drawer') this.openCartDrawer();
      }
    } catch (error) {
      addToCartBtn.innerHTML = originalHTML;
      alert(error.message || 'Failed to add item to cart. Please try again.');
      addToCartBtn.disabled = false;
    }
  }

  showAddedFeedback(button, originalHTML) {
    button.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8L6.5 11.5L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    setTimeout(() => {
      button.disabled = false;
      button.innerHTML = originalHTML;
    }, 2000);
  }

  openCartDrawer() {
    const drawerElement = document.querySelector('.cart-drawer-wrap');
    if (drawerElement) {
      drawerElement.classList.add('active');
      document.body.classList.add('drawer-open');
    }
    if (window.ajaxCart && typeof window.ajaxCart.updateCartDrawerContent === 'function') {
      window.ajaxCart.updateCartDrawerContent();
    }
  }

  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });

    const cartIcons = document.querySelectorAll('[data-cart-icon]');
    cartIcons.forEach(icon => {
      const itemText = count === 1 ? 'item' : 'items';
      icon.setAttribute('aria-label', `Cart (${count} ${itemText})`);
    });

    const cartBubbles = document.querySelectorAll('.cart-count-bubble, .cart-badge');
    cartBubbles.forEach(bubble => {
      bubble.textContent = count;
      bubble.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductCardSwatches();
});

if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ProductCardSwatches();
  });
}

window.ProductCardSwatches = ProductCardSwatches;